"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { usePrefetchWaveData } from "@/hooks/usePrefetchWaveData";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { CollapsedWave } from "./subcomponents/CollapsedWave";
import { ExpandedWave } from "./subcomponents/ExpandedWave";
import { useWaveNavigation } from "./hooks/useWaveNavigation";
import { useWaveNameTruncation } from "./hooks/useWaveNameTruncation";
import { formatWaveName } from "./utils/formatWaveName";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import type { MinimalWave } from "@/contexts/wave/hooks/useEnhancedWavesListCore";
interface WebBrainLeftSidebarWaveProps {
  readonly wave: MinimalWave;
  readonly onHover: (waveId: string) => void;
  readonly showPin?: boolean | undefined;
  readonly basePath?: string | undefined;
  readonly collapsed?: boolean | undefined;
}
const TOOLTIP_PLACEMENT = "right" as const;
const WebBrainLeftSidebarWave = t0 => {
  const $ = _c(41);
  const {
    wave,
    onHover,
    showPin: t1,
    basePath: t2,
    collapsed: t3
  } = t0;
  const showPin = t1 === undefined ? true : t1;
  const basePath = t2 === undefined ? "/waves" : t2;
  const collapsed = t3 === undefined ? false : t3;
  const {
    activeWave
  } = useMyStream();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let t4;
  if ($[0] !== activeWave.id || $[1] !== activeWave.set || $[2] !== basePath || $[3] !== hasTouchScreen || $[4] !== onHover || $[5] !== pathname || $[6] !== prefetchWaveData || $[7] !== searchParams || $[8] !== wave.firstUnreadDropSerialNo || $[9] !== wave.id) {
    t4 = {
      basePath,
      activeWaveId: activeWave.id,
      setActiveWave: activeWave.set,
      onHover,
      prefetchWaveData,
      pathname,
      searchParams,
      waveId: wave.id,
      hasTouchScreen,
      firstUnreadDropSerialNo: wave.firstUnreadDropSerialNo
    };
    $[0] = activeWave.id;
    $[1] = activeWave.set;
    $[2] = basePath;
    $[3] = hasTouchScreen;
    $[4] = onHover;
    $[5] = pathname;
    $[6] = prefetchWaveData;
    $[7] = searchParams;
    $[8] = wave.firstUnreadDropSerialNo;
    $[9] = wave.id;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    href,
    isActive,
    onMouseEnter,
    onClick
  } = useWaveNavigation(t4);
  let t5;
  if ($[11] !== wave.name || $[12] !== wave.type) {
    t5 = formatWaveName({
      name: wave.name,
      type: wave.type
    });
    $[11] = wave.name;
    $[12] = wave.type;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const formattedWaveName = t5;
  let t6;
  if ($[14] !== collapsed || $[15] !== formattedWaveName) {
    t6 = {
      collapsed,
      contentKey: formattedWaveName
    };
    $[14] = collapsed;
    $[15] = formattedWaveName;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    nameRef,
    isNameTruncated
  } = useWaveNameTruncation(t6);
  const isDropWave = wave.type !== ApiWaveType.Chat;
  const haveNewDrops = wave.newDropsCount.count > 0;
  const tooltipId = `wave-collapsed-${wave.id}`;
  const expandedTooltipId = `wave-expanded-${wave.id}`;
  const showCollapsedTooltip = collapsed && !hasTouchScreen;
  const showExpandedTooltip = !collapsed && !hasTouchScreen && isNameTruncated;
  if (collapsed) {
    let t7;
    if ($[17] !== formattedWaveName || $[18] !== haveNewDrops || $[19] !== href || $[20] !== isActive || $[21] !== isDropWave || $[22] !== onClick || $[23] !== onMouseEnter || $[24] !== showCollapsedTooltip || $[25] !== tooltipId || $[26] !== wave) {
      t7 = <CollapsedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} onMouseEnter={onMouseEnter} onClick={onClick} showTooltip={showCollapsedTooltip} tooltipId={tooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} />;
      $[17] = formattedWaveName;
      $[18] = haveNewDrops;
      $[19] = href;
      $[20] = isActive;
      $[21] = isDropWave;
      $[22] = onClick;
      $[23] = onMouseEnter;
      $[24] = showCollapsedTooltip;
      $[25] = tooltipId;
      $[26] = wave;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    return t7;
  }
  let t7;
  if ($[28] !== expandedTooltipId || $[29] !== formattedWaveName || $[30] !== haveNewDrops || $[31] !== href || $[32] !== isActive || $[33] !== isDropWave || $[34] !== nameRef || $[35] !== onClick || $[36] !== onMouseEnter || $[37] !== showExpandedTooltip || $[38] !== showPin || $[39] !== wave) {
    t7 = <ExpandedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} latestDropTimestamp={wave.newDropsCount.latestDropTimestamp} nameRef={nameRef} onMouseEnter={onMouseEnter} onClick={onClick} showExpandedTooltip={showExpandedTooltip} showPin={showPin} tooltipContent={formattedWaveName} tooltipId={expandedTooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} waveId={wave.id} isPinned={wave.isPinned} />;
    $[28] = expandedTooltipId;
    $[29] = formattedWaveName;
    $[30] = haveNewDrops;
    $[31] = href;
    $[32] = isActive;
    $[33] = isDropWave;
    $[34] = nameRef;
    $[35] = onClick;
    $[36] = onMouseEnter;
    $[37] = showExpandedTooltip;
    $[38] = showPin;
    $[39] = wave;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  return t7;
};
export default WebBrainLeftSidebarWave;